import { Box, Container, ThemeProvider, Typography } from "@mui/material";
import AppBaseScreen from "../../components/AppBaseScreen";
import { theme } from "./theme";
import SumsubWebSdk from "@sumsub/websdk-react";
import { useEffect, useState } from "react";
import {
  checkAmlbotStatus,
  checkSumSubStatus,
  createAmlbotApplicant,
  createSumsubApplicant,
  isPayeeKycAmlbot,
  parseAmlbotCreateResponse,
} from "../../store/api";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { showMessageBar } from "../../store/actions";
import { MESSAGE_BAR_TYPES } from "../../utils/constants.js";
import { useTranslation } from "react-i18next";
import SumsubLogo from "../../assets/logo/sumsub-long.svg";
import Rocket from "../../assets/logo/rocket.svg";
import Check from "../../assets/logo/check.svg";
import RocketfuelFUllLogo from "../../assets/logo/rocketfuel-fulllogo.svg";
import AppButton from "../../components/AppButton";
import QuestionIcon from "../../assets/logo/question-line.svg";
import config from "../../utils/config";

const isAmlbotKyc = isPayeeKycAmlbot();
const AMLBOT_IFRAME_MIN_HEIGHT = "90vh";

const shouldOpenAmlbotAsTopLevelPage = () => {
  const userAgent = window.navigator.userAgent;
  const isAppleDevice =
    /iPad|iPhone|iPod|Macintosh/i.test(userAgent) ||
    /Mac/i.test(window.navigator.platform);
  const isSafariBrowser =
    /Safari/i.test(userAgent) && !/Chrome|Chromium|CriOS|Edg/i.test(userAgent);

  return isAppleDevice || isSafariBrowser;
};

const VerificationLoading = ({ message }) => (
  <Box
    display="flex"
    flexDirection="column"
    justifyContent="center"
    alignItems="center"
    minHeight={AMLBOT_IFRAME_MIN_HEIGHT}
    width="100%"
  >
    <Box marginBottom={4} position="relative" width={84} height={84}>
      <Box className="spin" position="absolute" />
      <Rocket style={{ width: 84, position: "relative" }} />
    </Box>
    {message && (
      <Typography
        fontSize={16}
        fontWeight={400}
        color="#D4D4D8"
        marginTop={4}
        textAlign="center"
      >
        {message}
      </Typography>
    )}
  </Box>
);

const AmlbotVerificationIframe = ({ src, title, onLoaded }) => (
  <iframe
    src={src}
    title={title}
    width="100%"
    style={{
      minHeight: AMLBOT_IFRAME_MIN_HEIGHT,
      height: AMLBOT_IFRAME_MIN_HEIGHT,
      border: 0,
      display: "block",
    }}
    allow="camera; microphone; fullscreen"
    allowFullScreen
    onLoad={onLoaded}
  />
);

const Verification = () => {
  const [sumsubAccessToken, setSumsubAccessToken] = useState(null);
  const [amlbotLink, setAmlbotLink] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(isAmlbotKyc);
  const [iframeLoading, setIframeLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [status, setStatus] = useState(0);
  const options = {};
  const sumsubConfig = {};

  const kycActive = !!(sumsubAccessToken || amlbotLink);
  const showAmlbotSessionLoader =
    isAmlbotKyc && status !== 2 && sessionLoading;

  useEffect(() => {
    if (amlbotLink) {
      setIframeLoading(true);
    }
  }, [amlbotLink]);

  useEffect(() => {
    initializeKyc();
    const interval = setInterval(checkStatus, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializeKyc = async () => {
    if (isAmlbotKyc) {
      setSessionLoading(true);
    }
    try {
      const sta = await checkStatus();
      // eslint-disable-next-line eqeqeq
      if (sta == 2) {
        return;
      }
      if (isAmlbotKyc) {
        const data = await createAmlbotApplicant();
        const parsed = parseAmlbotCreateResponse(data);
        if (!parsed) {
          dispatch(
            showMessageBar(
              MESSAGE_BAR_TYPES.ERROR,
              t("verification.sessionError")
            )
          );
          return;
        }
        if (shouldOpenAmlbotAsTopLevelPage()) {
          window.location.assign(parsed.link);
          return;
        }
        setAmlbotLink(parsed.link);
      } else {
        const data = await createSumsubApplicant();
        setSumsubAccessToken(data.result.data);
      }
    } catch (err) {
      dispatch(showMessageBar(MESSAGE_BAR_TYPES.ERROR, err));
      navigate("/home");
    } finally {
      if (isAmlbotKyc) {
        setSessionLoading(false);
      }
    }
  };

  const checkStatus = async () => {
    try {
      const response = isAmlbotKyc
        ? await checkAmlbotStatus()
        : await checkSumSubStatus();
      const { kycStatus: status } = response.result;
      setStatus(parseInt(status.toString()));
      return parseInt(status.toString());
    } catch (err) {
      dispatch(showMessageBar(MESSAGE_BAR_TYPES.ERROR, err));
    }
  };

  const errorHandler = async () => {
    navigate("/home");
  };

  const accessTokenExpirationHandler = async () => {
    navigate("/home");
  };

  const pendingFooter = (
    <Box marginTop={1} maxWidth={500} margin="auto">
      <Typography fontSize={14} color="#F8FAFF" textAlign={"center"}>
        {t("kyt.pendingscreen")}
      </Typography>
      <AppButton
        title={t("go.dashboard")}
        onClick={() => navigate("/home")}
      />
      <AppButton
        href={config.KYC_WHY}
        target="_blank"
        sx={{ fontSize: 14, padding: 0 }}
        startIcon={<QuestionIcon />}
        buttontheme={"transparent"}
        title={t("learn.whyKyc")}
      />
    </Box>
  );

  return (
    <AppBaseScreen
      isHeader={
        (kycActive && [0, 1].includes(status)) ||
        (isAmlbotKyc && status !== 2 && amlbotLink && !iframeLoading)
      }
      navigate={true}
      navigatePath="/home"
    >
      <ThemeProvider theme={theme}>
        {isAmlbotKyc && status !== 2 && (
          <Container
            disableGutters
            sx={{ marginTop: "2vh", maxWidth: 900, width: "100%" }}
          >
            {showAmlbotSessionLoader && (
              <VerificationLoading message={t("verificationInit")} />
            )}
            {amlbotLink && (
              <Box position="relative" width="100%">
                {iframeLoading && (
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    zIndex={1}
                    bgcolor="transparent"
                  >
                    <VerificationLoading
                      message={t("verification.iframeLoading")}
                    />
                  </Box>
                )}
                <AmlbotVerificationIframe
                  src={amlbotLink}
                  title={t("verification.amlbotFrame")}
                  onLoaded={() => setIframeLoading(false)}
                />
              </Box>
            )}
            {amlbotLink && !iframeLoading && status !== 0 && status !== 2 && status !== 5 && pendingFooter}
          </Container>
        )}
        {!isAmlbotKyc && kycActive && status !== 2 && (
          <Container
            disableGutters
            sx={{ marginTop: "2vh", maxWidth: 900, width: "100%" }}
          >
            {sumsubAccessToken && (
              <SumsubWebSdk
                accessToken={sumsubAccessToken}
                expirationHandler={accessTokenExpirationHandler}
                config={sumsubConfig}
                options={options}
                onError={errorHandler}
              />
            )}
            {status !== 0 && status !== 2 && status !== 5 && pendingFooter}
            {status === 2 && (
              <Box marginTop={1} maxWidth={500} margin="auto">
                <Typography fontSize={14} color="#F8FAFF" textAlign={"center"}>
                  {t("kyt.pendingscreensuccess")}
                </Typography>
                <AppButton
                  title={t("go.dashboard")}
                  onClick={() => navigate("/home")}
                />
                <AppButton
                  href={config.KYC_WHY}
                  target="_blank"
                  sx={{ fontSize: 14, padding: 0 }}
                  startIcon={<QuestionIcon />}
                  buttontheme={"transparent"}
                  title={t("learn.whyKyc")}
                />
              </Box>
            )}
          </Container>
        )}
        {!isAmlbotKyc && !kycActive && status !== 2 && (
          <Container
            maxWidth={"xs"}
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              display={"flex"}
              flexDirection="column"
              justifyContent="center"
              alignItems={"center"}
              marginTop={"8%"}
              marginBottom={"2%"}
            >
              <Box marginTop={"7%"} marginBottom={5}>
                <SumsubLogo style={{ width: 228 }} />
              </Box>
              <Box marginTop={"25%"} marginBottom={20} marginLeft={"-84px"}>
                <Box className="spin" position={"absolute"}></Box>
                <Rocket style={{ width: 84, position: "absolute" }} />
              </Box>
              <Box marginTop={5} marginBottom={5}>
                <Typography
                  fontSize={20}
                  fontWeight={700}
                  color="#D4D4D8"
                  textAlign={"center"}
                >
                  {t("taking.toSumsub")}
                </Typography>
                <Typography
                  color={"#D4D4D8"}
                  fontSize={16}
                  fontWeight={400}
                  marginTop={2}
                  textAlign="center"
                >
                  {t("will.back")}
                </Typography>
              </Box>
            </Box>
            <Box>
              <AppButton
                href={config.KYC_WHY}
                target="_blank"
                sx={{ fontSize: 14, padding: 0 }}
                startIcon={<QuestionIcon />}
                buttontheme={"transparent"}
                title={t("learn.whyKyc")}
              />
            </Box>
          </Container>
        )}
        {status === 2 && (
          <Container maxWidth={"xs"}>
            <Box
              display={"flex"}
              flexDirection="column"
              justifyContent="center"
              alignItems={"center"}
              marginTop={10}
              marginBottom={10}
            >
              <Box marginTop={10} marginBottom={5}>
                <RocketfuelFUllLogo style={{ width: "100%" }} />
              </Box>
              <Box marginTop={8} marginBottom={8}>
                <Check style={{ width: 84, height: 84 }} />
              </Box>
              <Box marginTop={5} marginBottom={5}>
                <Typography
                  fontSize={20}
                  fontWeight={700}
                  color="#D4D4D8"
                  textAlign={"center"}
                >
                  {t("Welcomeback")}
                </Typography>
                <Typography
                  color={"#D4D4D8"}
                  fontSize={16}
                  fontWeight={400}
                  marginTop={2}
                  textAlign="center"
                >
                  {t("kyc.verified")}
                </Typography>
              </Box>
              <AppButton
                title={t("go.dashboard")}
                onClick={() => navigate("/home")}
              />
            </Box>
            <Box>
              <AppButton
                href={config.KYC_WHY}
                target="_blank"
                sx={{ fontSize: 14 }}
                startIcon={<QuestionIcon />}
                buttontheme={"transparent"}
                title={t("learn.whyKyc")}
              />
            </Box>
          </Container>
        )}
      </ThemeProvider>
    </AppBaseScreen>
  );
};

export default Verification;
